import { useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from 'components/common/LoadingButton';
import {
  FEE_TYPE_FIXED,
  FEE_TYPE_PERCENTAGE,
  GET_TRANSACTION_FEES
} from 'components/donations/donation-settings/TransactionFees';
import AuthContext from 'context/authContext';
import PropTypes from 'prop-types';
import { useContext, useMemo, useState, useRef, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import ADAdvancedSettings from './donation-form-components/ADAdvancedSettings';
import ADDonationDetails from './donation-form-components/ADDonationDetails';
import ADSelectDonor from './donation-form-components/ADSelectDonor';
import ADSelectedAppeals from './donation-form-components/ADSelectedAppeals';
import { Card, Col, Nav, Row } from 'react-bootstrap';
import useScrollSpy from 'react-use-scrollspy';

// Side navigation links for the donation configuration
const sideNavLinks = [
  { id: 1, href: '#details', title: 'Donation Details' },
  { id: 2, href: '#appeals', title: 'Selected Appeals' },
  { id: 3, href: '#settings', title: 'Advanced Settings' }
];

export const calculatePercentageFee = (amount, percentage) => {
  return (amount * percentage) / 100;
};

const DonationConfig = ({ onSubmit, loading }) => {
  const { organization } = useContext(AuthContext);
  const [stickyStatus, setStickyStatus] = useState(false);

  // Set up scroll spy for navigation
  const sectionRefs = [useRef(null), useRef(null), useRef(null)];
  const activeSection = useScrollSpy({
    sectionElementRefs: sectionRefs,
    offsetPx: -150
  });

  useEffect(() => {
    window.addEventListener('scroll', () => {
      if (window.scrollY < 100) {
        setStickyStatus(false);
      } else {
        setStickyStatus(true);
      }
    });

    return () => {
      window.removeEventListener('scroll', () => {});
    };
  }, []);

  const [selectedAppeal, setSelectedAppeal] = useState();
  const [index, setIndex] = useState();
  const [donationItems, setDonationItems] = useState([]);
  const { data: transactionFeesData } = useQuery(GET_TRANSACTION_FEES, {
    variables: {
      organisation_id: organization.id
    }
  });
  const [searchParams] = useSearchParams();
  const donorId = searchParams.get('donorId');

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      donorId: donorId,
      settings: {
        coverTransactionFee: false,
        giftAid: false
      }
    },
    resolver: yupResolver(
      Yup.object().shape({
        donorId: Yup.string().required('Required Field')
      })
    )
  });

  const { handleSubmit: RHFHandleSubmit, watch } = methods;
  const coverTransactionFee = watch('settings.coverTransactionFee');
  // useEffect(() => {
  //   if (donorId) {
  //     setValue('donorId', donorId);
  //   }
  // }, [donorId, setValue]);

  const handleSubmit = data => {
    const transactionFees = data.settings.coverTransactionFee
      ? totalTransactionFee
      : 0;
    if (donationItems.length === 0) {
      toast.error('Please add a package');
    } else
      onSubmit({
        ...data,
        donationItems,
        subtotal: totalDonationAmount,
        transactionFees,
        total: totalDonationAmount + transactionFees
      });
  };

  const totalDonationAmount = useMemo(() => {
    return donationItems.reduce((acc, curr) => {
      acc += curr.amount;
      return acc;
    }, 0);
  }, [donationItems]);
  const totalTransactionFee = useMemo(() => {
    if (!transactionFeesData) return 0;

    return transactionFeesData.transaction_fee.reduce((acc, curr) => {
      if (curr.fee_type === FEE_TYPE_PERCENTAGE) {
        acc = calculatePercentageFee(totalDonationAmount, curr.amount);
      } else if (curr.fee_type === FEE_TYPE_FIXED) {
        acc += curr.amount;
      }
      return acc;
    }, 0);
  }, [transactionFeesData, totalDonationAmount, coverTransactionFee]);

  const onAddNewDonationItem = data => {
    if (index === undefined) {
      setDonationItems(prevState => {
        const newState = [...prevState];
        newState.push(data);
        return newState;
      });
    } else {
      setDonationItems(prevState => {
        const newState = [...prevState];
        newState[index] = data;
        return newState;
      });
      setIndex(null);
      setSelectedAppeal(null);
    }
  };

  const onRemoveDonationItem = index => {
    setDonationItems(prevState => {
      const newState = [...prevState];
      newState.splice(index, 1);
      return newState;
    });
  };

  const onEditDonationItemClick = index => {
    setSelectedAppeal(donationItems[index]);
    setIndex(index);
  };

  return (
    <Row className="g-3">
      <Col md={9}>
        <FormProvider {...methods}>
          <div ref={sectionRefs[0]}>
            <ADSelectDonor donorId={donorId} id="details" />
          </div>
          <div>
            <ADDonationDetails
              data={selectedAppeal?.values}
              onSubmit={onAddNewDonationItem}
            />
          </div>
          <div ref={sectionRefs[1]}>
            <ADSelectedAppeals
              items={donationItems}
              subtotal={totalDonationAmount}
              total={
                totalDonationAmount +
                (coverTransactionFee ? totalTransactionFee : 0)
              }
              transactionFee={coverTransactionFee ? totalTransactionFee : 0}
              onEdit={onEditDonationItemClick}
              onRemove={onRemoveDonationItem}
              id="appeals"
            />
          </div>
          <div ref={sectionRefs[2]}>
            <ADAdvancedSettings
              transactionFee={totalTransactionFee}
              id="settings"
            />
          </div>
          {/* <ADScheduleDonations /> */}
          <LoadingButton
            loading={loading}
            size="lg"
            variant="success"
            className="w-100 py-3 mt-3 fs-0 fs-md-1"
            onClick={RHFHandleSubmit(handleSubmit)}
          >
            Save And Continue To Payments
          </LoadingButton>
        </FormProvider>
      </Col>
      <Col md={3} className="order-0 order-md-1 d-none d-md-block">
        <Card
          className={'mb-3 sticky-top border shadow-none'}
          style={{ top: stickyStatus ? '5rem' : '0' }}
        >
          <Card.Body>
            <div>
              <Nav className="flex-column" variant="pills">
                {sideNavLinks?.map(item => (
                  <Nav.Link
                    key={item.id}
                    href={item.href}
                    className={`${
                      activeSection === item.id
                        ? 'current custom-nav-link'
                        : 'custom-nav-link'
                    }`}
                    active={activeSection === item.id - 1}
                  >
                    {item.title}
                  </Nav.Link>
                ))}
              </Nav>
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

DonationConfig.propTypes = {
  onSubmit: PropTypes.func,
  loading: PropTypes.bool
};

export default DonationConfig;
